import React, { useEffect, useState, useCallback } from "react";
import { Box, useMediaQuery } from "@mui/material";
import {
  GpsFixed as GpsFixedIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { RootState } from "../../../core/store";
import {
  fetchActiveSessions,
  TrackingSession,
  TrackingState,
} from "../../../shared/tracking/trackingSlice";
import useTrackingHub from "../../../shared/tracking/hooks/useTrackingHub";
import useTranslation from "../../../shared/hooks/useTranslation";
import { dtGreen } from "../../../shared/themes/redesignTokens";
import TrackingMapContainer from "./map/TrackingMapContainer";
import ActiveServiceCard from "./ActiveServiceCard";
import LivePulseDot from "./LivePulseDot";

// ===================================================================
//  KEYFRAMES — injected once globally
// ===================================================================

const TRACKING_KEYFRAMES = `
@keyframes livePulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.6); }
}
@keyframes technicianPulse {
  0%, 100% { opacity: 0.6; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.15); }
}
@keyframes technicianPulseOuter {
  0%, 100% { opacity: 0.2; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.2); }
}
@keyframes technicianAmberPulse {
  0%, 100% { opacity: 0.5; transform: scale(1); border-color: #F59E0B; }
  50% { opacity: 1; transform: scale(1.2); border-color: #D97706; }
}
@keyframes technicianAmberPulseOuter {
  0%, 100% { opacity: 0.15; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(1.25); }
}
@keyframes etaCardEntrance {
  0% { opacity: 0; transform: translateX(-50%) translateY(-12px); }
  100% { opacity: 1; transform: translateX(-50%) translateY(0); }
}
@keyframes etaCheckPop {
  0% { transform: scale(0); }
  60% { transform: scale(1.3); }
  100% { transform: scale(1); }
}
@keyframes trackingTabEntrance {
  0% { opacity: 0; transform: translateX(20px); }
  100% { opacity: 1; transform: translateX(0); }
}
@keyframes trackingPanelSlideIn {
  0% { transform: translateX(100%); }
  100% { transform: translateX(0); }
}
@keyframes trackingPanelSlideUp {
  0% { transform: translateY(100%); }
  100% { transform: translateY(0); }
}
@keyframes markerPulse {
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.3); }
}
@keyframes confettiBurst {
  0% {
    opacity: 1;
    transform: translate(0, 0) rotate(0deg) scale(1);
  }
  30% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translate(var(--dx, 0), var(--dy, -150px)) rotate(var(--rot, 360deg)) scale(0.3);
  }
}
@keyframes connectionBannerIn {
  0% { opacity: 0; transform: translateY(-8px); }
  100% { opacity: 1; transform: translateY(0); }
}
`;

// ===================================================================
//  SLIDING TAB — 3-state collapsible panel on the right side
// ===================================================================
//
//  State 1 (no active): 1px green strip, pops slightly on hover
//  State 2 (active, closed): small badge "1 Active Service"
//  State 3 (user clicks): fully opened panel with map + cards
// ===================================================================

type TabState = "hidden" | "badge" | "open";

const STATIONARY_THRESHOLD_MS = 5 * 60 * 1000;

const SlidingServiceTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation(["serviceTracking"]);

  // Inject tracking keyframes once
  useEffect(() => {
    const styleId = "service-tracking-keyframes";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = TRACKING_KEYFRAMES;
      document.head.appendChild(style);
    }
  }, []);

  const isPhone = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(min-width:768px) and (max-width:1199px)");

  const {
    activeSessions,
    positions,
  } = useAppSelector<TrackingState>((state: RootState) => state.tracking);

  const [tabState, setTabState] = useState<TabState>("hidden");
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [hovered, setHovered] = useState(false);

  // Fetch active sessions on mount (and periodically)
  useEffect(() => {
    dispatch(fetchActiveSessions());
    const interval = setInterval(() => {
      dispatch(fetchActiveSessions());
    }, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [dispatch]);

  // Auto-transition between states based on active sessions
  useEffect(() => {
    if (activeSessions.length === 0) {
      setTabState("hidden");
      setSelectedSessionId(null);
    } else if (tabState === "hidden") {
      setTabState("badge");
    }
  }, [activeSessions.length, tabState]);

  // Auto-select first session
  useEffect(() => {
    if (activeSessions.length > 0 && !selectedSessionId) {
      setSelectedSessionId(activeSessions[0].id);
    }
  }, [activeSessions, selectedSessionId]);

  // Subscribe to selected session's SignalR updates
  const { latestPosition } = useTrackingHub(
    tabState === "open" ? selectedSessionId : null
  );

  // Selected session + current position
  const selectedSession =
    activeSessions.find((s) => s.id === selectedSessionId) || null;

  const currentPosition = latestPosition
    || (selectedSessionId && positions[selectedSessionId])
    || null;

  const isStationary = React.useMemo(() => {
    if (!currentPosition?.recordedAt) return false;
    const lastUpdateTime = new Date(currentPosition.recordedAt).getTime();
    return Date.now() - lastUpdateTime > STATIONARY_THRESHOLD_MS;
  }, [currentPosition?.recordedAt]);

  const handleTabClick = useCallback(() => {
    setTabState((prev) => (prev === "open" ? "badge" : "open"));
  }, []);

  const handleClose = useCallback(() => {
    setTabState("badge");
  }, []);

  const handleSessionClick = useCallback((session: TrackingSession) => {
    setSelectedSessionId(session.id);
  }, []);

  // Panel width: match MIDAS panel width
  const panelWidth = isPhone ? "100vw" : isTablet ? "380px" : "420px";

  // ─── STATE 1: No active services — 1px green strip ───
  if (tabState === "hidden") {
    return (
      <Box
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          position: "fixed",
          bottom: "170px", // Above MIDAS bubble
          right: 0,
          width: hovered ? "4px" : "1px",
          height: "60px",
          backgroundColor: dtGreen.green500,
          borderRadius: "4px 0 0 4px",
          transition: "all 0.3s ease-out",
          zIndex: 1399,
          cursor: "default",
          opacity: hovered ? 0.8 : 0.3,
        }}
      />
    );
  }

  // ─── STATE 2: Active service — badge tab ───
  if (tabState === "badge") {
    return (
      <Box
        onClick={handleTabClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          position: "fixed",
          bottom: "170px", // Above MIDAS bubble
          right: 0,
          width: hovered ? "40px" : "36px",
          height: "120px",
          borderRadius: "10px 0 0 10px",
          background: `linear-gradient(180deg, ${dtGreen.green500} 0%, ${dtGreen.green700} 100%)`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "6px",
          cursor: "pointer",
          zIndex: 1399,
          boxShadow: `-3px 2px 12px rgba(16,185,129,0.25), -1px 1px 4px rgba(0,0,0,0.08)`,
          transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
          animation: "trackingTabEntrance 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both",
          "&:hover": {
            boxShadow: `-4px 4px 20px rgba(16,185,129,0.35), -2px 2px 8px rgba(0,0,0,0.1)`,
          },
          "&:active": {
            width: "34px",
            transition: "width 0.1s ease",
          },
        }}
      >
        <GpsFixedIcon sx={{ fontSize: "18px", color: "#FFFFFF" }} />

        {/* Live pulse dot */}
        <Box sx={{ position: "relative", width: 8, height: 8 }}>
          <LivePulseDot size={8} color="#FFFFFF" />
        </Box>

        {/* Count */}
        <Box
          sx={{
            fontFamily: dtGreen.fontMono,
            fontSize: "14px",
            fontWeight: 700,
            color: "#FFFFFF",
            lineHeight: 1,
          }}
        >
          {activeSessions.length}
        </Box>

        {/* Vertical label */}
        {"LIVE".split("").map((char, i) => (
          <Box
            key={i}
            component="span"
            sx={{
              color: "#FFFFFF",
              fontSize: "9px",
              fontWeight: 700,
              fontFamily: dtGreen.font,
              lineHeight: 1,
              letterSpacing: "0.5px",
              textShadow: "0 1px 2px rgba(0,0,0,0.15)",
            }}
          >
            {char}
          </Box>
        ))}
      </Box>
    );
  }

  // ─── STATE 3: Fully opened panel ───
  // Phone: full-screen overlay
  if (isPhone) {
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1350,
          backgroundColor: dtGreen.cardBg,
          display: "flex",
          flexDirection: "column",
          animation: "trackingPanelSlideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1) both",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: "12px 16px",
            borderBottom: `1px solid ${dtGreen.cardBorder}`,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontFamily: dtGreen.font,
              fontSize: "15px",
              fontWeight: 600,
              color: dtGreen.textPrimary,
            }}
          >
            <GpsFixedIcon sx={{ fontSize: 20, color: dtGreen.green500 }} />
            {translate("serviceTracking:title")}
            <LivePulseDot size={6} color={dtGreen.green500} />
          </Box>
          <Box
            onClick={handleClose}
            sx={{
              cursor: "pointer",
              p: "4px",
              borderRadius: dtGreen.radiusSm,
              "&:hover": { backgroundColor: dtGreen.greenGlow },
            }}
          >
            <CloseIcon sx={{ fontSize: 20, color: dtGreen.textSecondary }} />
          </Box>
        </Box>

        {/* Map: 55% */}
        <Box sx={{ height: "55%", flexShrink: 0 }}>
          <TrackingMapContainer
            session={selectedSession}
            position={currentPosition}
            isStationary={isStationary}
          />
        </Box>

        {/* Cards: 45% */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          {activeSessions.map((session) => (
            <ActiveServiceCard
              key={session.id}
              session={session}
              position={positions[session.id] || null}
              isSelected={session.id === selectedSessionId}
              onClick={handleSessionClick}
            />
          ))}
        </Box>
      </Box>
    );
  }

  // Desktop / Tablet: side panel
  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        width: panelWidth,
        zIndex: 1350,
        boxShadow: "-8px 0 40px rgba(0, 0, 0, 0.12), -2px 0 12px rgba(0, 0, 0, 0.06)",
        backgroundColor: dtGreen.cardBg,
        display: "flex",
        flexDirection: "column",
        animation: "trackingPanelSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1) both",
        borderLeft: `1px solid ${dtGreen.cardBorder}`,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: "14px 16px",
          borderBottom: `1px solid ${dtGreen.cardBorder}`,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            fontFamily: dtGreen.font,
            fontSize: "15px",
            fontWeight: 600,
            color: dtGreen.textPrimary,
          }}
        >
          <GpsFixedIcon sx={{ fontSize: 20, color: dtGreen.green500 }} />
          {translate("serviceTracking:title")}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              backgroundColor: dtGreen.green50,
              border: `1px solid ${dtGreen.green100}`,
              borderRadius: dtGreen.radiusPill,
              padding: "2px 8px",
            }}
          >
            <LivePulseDot size={6} color={dtGreen.green500} />
            <Box
              component="span"
              sx={{
                fontFamily: dtGreen.fontMono,
                fontSize: "11px",
                fontWeight: 700,
                color: dtGreen.green600,
              }}
            >
              {activeSessions.length}
            </Box>
          </Box>
        </Box>
        <Box
          onClick={handleClose}
          sx={{
            cursor: "pointer",
            p: "4px",
            borderRadius: dtGreen.radiusSm,
            transition: "all 0.2s",
            "&:hover": { backgroundColor: dtGreen.greenGlow },
          }}
        >
          <CloseIcon sx={{ fontSize: 20, color: dtGreen.textSecondary }} />
        </Box>
      </Box>

      {/* Map: 50% */}
      <Box sx={{ height: "50%", flexShrink: 0 }}>
        <TrackingMapContainer
          session={selectedSession}
          position={currentPosition}
          isStationary={isStationary}
        />
      </Box>

      {/* Cards: 50% */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: "12px",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: dtGreen.textMuted,
            borderRadius: 2,
          },
        }}
      >
        {activeSessions.map((session) => (
          <ActiveServiceCard
            key={session.id}
            session={session}
            position={positions[session.id] || null}
            isSelected={session.id === selectedSessionId}
            onClick={handleSessionClick}
          />
        ))}
      </Box>
    </Box>
  );
};

export default SlidingServiceTab;
